import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit2, Check, X, ChevronDown } from 'lucide-react';
import type { TranscriptSegment, Speaker } from '@/types';
import { formatTimestamp, formatAbsoluteTime } from '@/utils/parser';
import { useTranscriptStore } from '@/store';

interface SegmentListProps {
  segments: TranscriptSegment[];
  speakers: Speaker[];
  recordingDate: Date | string;
  showAbsoluteTime: boolean;
}

export function SegmentList({ segments, speakers, recordingDate, showAbsoluteTime }: SegmentListProps) {
  const updateSegment = useTranscriptStore((state) => state.updateSegment);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editSpeaker, setEditSpeaker] = useState('');
  const [showSpeakerDropdown, setShowSpeakerDropdown] = useState(false);

  const speakerMap = new Map(speakers.map((s) => [s.id, s]));
  
  // Helper function to format time based on mode
  const formatTime = (ms: number) => {
    return showAbsoluteTime 
      ? formatAbsoluteTime(ms, recordingDate)
      : formatTimestamp(ms);
  };

  const startEditing = (segment: TranscriptSegment) => {
    setEditingId(segment.id);
    setEditText(segment.text);
    setEditSpeaker(segment.speakerId);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditText('');
    setEditSpeaker('');
    setShowSpeakerDropdown(false);
  };

  const saveEditing = (segmentId: string) => {
    updateSegment(segmentId, {
      text: editText,
      speakerId: editSpeaker,
    });
    cancelEditing();
  };

  if (segments.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500 dark:text-slate-400">
        No segments to display. Try adjusting your filters.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <AnimatePresence>
        {segments.map((segment, index) => {
          const speaker = speakerMap.get(segment.speakerId);
          const isEditing = editingId === segment.id;

          return (
            <motion.div
              key={segment.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ delay: index * 0.02 }}
              className={`segment-card group ${isEditing ? 'selected' : ''}`}
            >
              {/* Header */}
              <div className="flex items-start gap-3 mb-2">
                {/* Speaker Badge */}
                {isEditing ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowSpeakerDropdown(!showSpeakerDropdown)}
                      className="speaker-badge bg-slate-100 dark:bg-slate-800"
                      style={{ borderColor: speakerMap.get(editSpeaker)?.color }}
                    >
                      <span
                        className="dot"
                        style={{ backgroundColor: speakerMap.get(editSpeaker)?.color }}
                      />
                      <span className="text-slate-900 dark:text-white">
                        {speakerMap.get(editSpeaker)?.name ?? 'Unknown'}
                      </span>
                      <ChevronDown className="w-3 h-3 text-slate-400" />
                    </button>

                    {showSpeakerDropdown && (
                      <div className="absolute z-10 top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                        {speakers.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => {
                              setEditSpeaker(s.id);
                              setShowSpeakerDropdown(false);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
                              editSpeaker === s.id ? 'bg-sky-50 dark:bg-sky-900/20' : ''
                            }`}
                          >
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: s.color }}
                            />
                            {s.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="speaker-badge"
                    style={{ backgroundColor: `${speaker?.color}20`, color: speaker?.color }}
                  >
                    <span className="dot" style={{ backgroundColor: speaker?.color }} />
                    {speaker?.name ?? 'Unknown'}
                  </div>
                )}

                {/* Timestamp */}
                <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                  {formatTime(segment.startTime)} ({Math.round((segment.endTime - segment.startTime) / 1000)}s)
                </span>

                {/* Edit indicator */}
                {segment.edited && !isEditing && (
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                    Edited
                  </span>
                )}

                {/* Actions */}
                <div className="ml-auto flex items-center gap-1">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => saveEditing(segment.id)}
                        className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/50"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => startEditing(segment)}
                      className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-all"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Text Content */}
              {isEditing ? (
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white text-sm resize-none focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none"
                  rows={3}
                  autoFocus
                />
              ) : (
                <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                  {segment.text}
                </p>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
